import { SLOT_TO_ROLE, ASSIGN_ALL_ORDER_TYPES, WILDCARD_ORDER_TYPE } from './constants';
import { uniq, zipObject } from 'lodash';
import { SUBREGION_IMPORT_ATTRIBUTES } from '../constants';

// Builds the delivery_profile_assignments insert rows for applying a profile to
// a shipper. One row per selected order_type; selecting ASSIGN_ALL_ORDER_TYPES
// collapses to a single row carrying the WILDCARD_ORDER_TYPE sentinel ("any
// order type"). zip_type is chosen in the apply bar (defaults to DROPOFF).
export const buildAssignmentRows = ({ deliveryProfile, shipperId, zipType, orderTypes }) => {
    const zip_type = zipType || 'DROPOFF';
    const base = { delivery_profile_id: deliveryProfile?.delivery_profile_id, shipper_id: shipperId, zip_type };

    if (!orderTypes?.length) return [];
    if (orderTypes.includes(ASSIGN_ALL_ORDER_TYPES)) return [{ ...base, order_type: WILDCARD_ORDER_TYPE }];

    return uniq(orderTypes).map((order_type) => ({ ...base, order_type }));
};

// Filters carriers eligible for a mapping slot by their client role. A carrier
// may serve multiple slots when it holds multiple roles (e.g. CARRIER +
// MIDDLE_MILE_CARRIER).
export const filterCarriersByLeg = (slot, carriers) => {
    const requiredRole = SLOT_TO_ROLE[slot];
    if (!requiredRole) return [];
    return (carriers || []).filter((carrier) => !!carrier?.user?.roles?.[requiredRole]);
};

const UUID_RE = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/;

const hashMapping = (mapping) =>
    btoa(
        ['carrier_id', 'middle_mile_carrier_id', 'warehouse_carrier_id']
            .map((attr) => mapping[attr])
            .filter((id) => id)
            .join('|')
    );

export const parseImportedSubregions = (headers, body, ctx, existingSubregions) => {
    const ATTRS = SUBREGION_IMPORT_ATTRIBUTES;
    const { deliveryProfileId, carriersById, mmCarriersById, warehouseCarriersById } = ctx;

    const isValidMapping = (mapping) =>
        !!mapping.carrier_id &&
        !!carriersById[mapping.carrier_id] &&
        (!mapping.middle_mile_carrier_id || !!mmCarriersById[mapping.middle_mile_carrier_id]) &&
        (!mapping.warehouse_carrier_id || !!warehouseCarriersById[mapping.warehouse_carrier_id]);

    const importedByName = body.reduce((acc, row, rowIndex) => {
        const parsed = zipObject(headers, row);
        if (Object.values(parsed).every((cell) => !cell)) return acc;

        Object.entries(parsed).forEach(([key, value]) => {
            switch (key) {
                case ATTRS.zip:
                    if (!value) throw new Error(`Row ${rowIndex + 1} is missing a zip code`);
                    if (value.length !== 5) throw new Error(`Row ${rowIndex + 1} has an invalid zip code`);
                    break;
                case ATTRS.carrier_id:
                case ATTRS.middle_mile_carrier_id:
                case ATTRS.warehouse_id:
                case ATTRS.warehouse_carrier_id:
                    if (value && !UUID_RE.test(value)) throw new Error(`Row ${rowIndex + 1} has an invalid Id`);
                    break;
            }
        });

        const subregionName = parsed[ATTRS.name];
        const prev = acc[subregionName]?.carriers || [];
        const mapping = {
            is_direct:
                !!parsed[ATTRS.is_direct] &&
                !['no', 'false'].includes(parsed[ATTRS.is_direct].toString().toLowerCase()),
            carrier_id: parsed[ATTRS.carrier_id] || null,
            warehouse_id: parsed[ATTRS.warehouse_id] || null,
            middle_mile_carrier_id: parsed[ATTRS.middle_mile_carrier_id] || null,
            warehouse_carrier_id: parsed[ATTRS.warehouse_carrier_id] || null,
            site_code: parsed[ATTRS.site_code] || null,
            site_contact_name: parsed[ATTRS.site_contact_name] || null,
            site_contact_email: parsed[ATTRS.site_contact_email] || null,
            site_contact_phone: parsed[ATTRS.site_contact_phone] || null,
        };
        const next = [
            ...prev,
            ...(isValidMapping(mapping) && !prev.find((m) => hashMapping(m) === hashMapping(mapping)) ? [mapping] : []),
        ];

        return {
            ...acc,
            [subregionName]: {
                zips: uniq([...(acc[subregionName]?.zips || []), parsed[ATTRS.zip]]),
                carriers: next,
            },
        };
    }, {});

    const to_remove = existingSubregions.filter((s) => !!importedByName[s.name]).map((s) => s.subregion_id);

    const to_add = Object.entries(importedByName).map(([name, { zips, carriers }]) => ({
        name,
        delivery_profile_id: deliveryProfileId,
        zips: { data: zips.map((zip) => ({ zip })) },
        carriers: {
            data: carriers.map((carrier, idx) => ({
                ...carrier,
                ...(idx === 0 ? { is_primary: true } : {}),
            })),
        },
    }));

    return { to_add, to_remove };
};

export const unitToMeters = (value, unit) => {
    const v = Number(value) || 0;
    return unit === 'km' ? v * 1000 : v * 1609.344;
};

// Equirectangular bounding box that contains the circle of given radius. Use
// to drive fitBounds so the viewport (and therefore loaded ZCTA tiles) covers
// the entire radius before querying.
export const getCircleBounds = (center, radiusMeters) => {
    const [lng, lat] = center;
    const earthRadius = 6371000;
    const latOffset = (radiusMeters / earthRadius) * (180 / Math.PI);
    const lngOffset = latOffset / Math.cos((lat * Math.PI) / 180);
    return [
        [lng - lngOffset, lat - latOffset],
        [lng + lngOffset, lat + latOffset],
    ];
};

const haversineMeters = (a, b) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(b[0] - a[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
};

const flattenPolygonCoords = (geometry) => {
    if (!geometry) return [];
    if (geometry.type === 'Polygon') return geometry.coordinates.flat();
    if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2);
    return [];
};

const averageCoord = (coords) => {
    if (!coords.length) return null;
    let sumLng = 0;
    let sumLat = 0;
    for (const [lng, lat] of coords) {
        sumLng += lng;
        sumLat += lat;
    }
    return [sumLng / coords.length, sumLat / coords.length];
};

// Returns the unique zips (NAME20) whose averaged centroid sits in the band
// between minMeters and maxMeters of the given center (inclusive on max,
// inclusive on min). Aggregates coordinates across all feature shards so a
// ZCTA split across tile boundaries gets a single, correct centroid.
export const findZipsInRadius = (map, center, minMeters, maxMeters) => {
    if (!map || !center || !maxMeters || maxMeters <= minMeters) return [];
    try {
        const features = map.queryRenderedFeatures({ layers: ['outlines-layer'] });
        const coordsByZip = new Map();
        for (const feature of features) {
            const zip = feature.properties?.NAME20;
            if (!zip) continue;
            const coords = flattenPolygonCoords(feature.geometry);
            if (!coords.length) continue;
            const existing = coordsByZip.get(zip);
            if (existing) {
                existing.push(...coords);
            } else {
                coordsByZip.set(zip, coords);
            }
        }
        const within = [];
        for (const [zip, coords] of coordsByZip) {
            const centroid = averageCoord(coords);
            if (!centroid) continue;
            const dist = haversineMeters(center, centroid);
            if (dist >= minMeters && dist <= maxMeters) {
                within.push(zip);
            }
        }
        return within.sort();
    } catch (_) {
        return [];
    }
};

export const serializeSubregionsForExport = (subregions, { genName }) =>
    (subregions || []).reduce((acc, subregion, idx) => {
        const name = genName(subregion, idx);
        const primary = (subregion.carriers || []).find((m) => m.is_primary);
        if (!primary) return acc;
        return [
            ...acc,
            ...subregion.zips.map(({ zip }) => ({
                name,
                zip,
                is_direct: primary.is_direct,
                carrier_id: primary.carrier_id,
                warehouse_id: primary.warehouse_id,
                middle_mile_carrier_id: primary.middle_mile_carrier_id,
            })),
        ];
    }, []);
